import { ensureFreshSession, isRecoverableSupabaseError, waitForBrowserOnline } from './sessionRecovery';

const DEFAULT_TIMEOUT_MS = 30000;
const DEFAULT_RETRIES = 1;

export const createTimeoutError = (label) => {
  const error = new Error(`${label} timed out while the portal was reconnecting. Please try again.`);
  error.name = 'TimeoutError';
  return error;
};

const runWithTimeout = (operation, label, timeoutMs = DEFAULT_TIMEOUT_MS, onTimeout) => {
  let timeoutId;

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      if (typeof onTimeout === 'function') onTimeout();
      reject(createTimeoutError(label));
    }, timeoutMs);
  });

  return Promise.race([
    Promise.resolve(operation),
    timeoutPromise,
  ]).finally(() => clearTimeout(timeoutId));
};

export const withOperationTimeout = async (operationOrFactory, label, timeoutMs = DEFAULT_TIMEOUT_MS, options = {}) => {
  const isFactory = typeof operationOrFactory === 'function';
  const retries = options.retries ?? (isFactory ? DEFAULT_RETRIES : 0);
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      await waitForBrowserOnline();
      await ensureFreshSession({ force: attempt > 0, label });
      const operation = isFactory ? operationOrFactory() : operationOrFactory;
      return await runWithTimeout(operation, label, timeoutMs, options.onTimeout);
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isFactory || !isRecoverableSupabaseError(error)) break;
    }
  }

  throw lastError;
};

export const withSupabaseQueryTimeout = async (queryOrFactory, label, timeoutMs = DEFAULT_TIMEOUT_MS, options = {}) => {
  const isFactory = typeof queryOrFactory === 'function';
  const retries = options.retries ?? (isFactory ? DEFAULT_RETRIES : 0);
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    let timedOut = false;

    try {
      await waitForBrowserOnline();
      await ensureFreshSession({ force: attempt > 0, label });

      const query = isFactory ? queryOrFactory() : queryOrFactory;
      const request = typeof query?.abortSignal === 'function'
        ? query.abortSignal(controller.signal)
        : query;

      return await runWithTimeout(
        Promise.resolve(request).then(null, (error) => {
          if (timedOut || error?.name === 'AbortError') {
            throw createTimeoutError(label);
          }
          throw error;
        }),
        label,
        timeoutMs,
        () => {
          timedOut = true;
          controller.abort();
        }
      );
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isFactory || !isRecoverableSupabaseError(error)) break;
    }
  }

  throw lastError;
};
